import { DASHBOARD_SETTINGS, getDashboardSubtitle } from "../fixtures";
import { buildApiUrl } from "../../../services/apiUrl";
import { authenticatedFetch } from "../../../lib/apiClient";
import type {
  DashboardRole,
  DashboardSettingsState,
  DashboardThread,
} from "../types.js";
import { getSeededDashboardMessages } from "../messages/api/messagesApi";
import type {
  DashboardBootstrapBackendAdapter,
  DashboardBootstrapContractResponse,
  DashboardBootstrapContractPayload,
  DashboardBootstrapPermissions,
  DashboardBootstrapSessionStateLike,
  DashboardBootstrapViewModel,
  DashboardBootstrapSnapshot,
} from "./types/dashboardBootstrap.api";

function normalizeDashboardRole(value: unknown): DashboardRole | null {
  if (value === "client" || value === "admin" || value === "superadmin") {
    return value;
  }

  return null;
}

export function getDashboardBadge(role: DashboardRole): string {
  switch (role) {
    case "client":
      return "Client Dashboard";
    case "superadmin":
      return "Superadmin Dashboard";
    case "admin":
    default:
      return "Admin Dashboard";
  }
}

export function getDashboardDisplayName(role: DashboardRole): string {
  return role === "superadmin" ? "Global Workspace" : "Industrial Hub";
}

export function getDashboardPermissions(
  role: DashboardRole | null,
): DashboardBootstrapPermissions {
  return {
    canCreateProjects: role === "client" || role === "admin" || role === "superadmin",
    canUploadAssets: role === "client" || role === "admin" || role === "superadmin",
    canSendMessages: role === "client" || role === "admin" || role === "superadmin",
    canManageSettings: role === "client" || role === "admin" || role === "superadmin",
  };
}

function normalizeContractRole(contractRoles: unknown[] | null | undefined): DashboardRole | null {
  if (!Array.isArray(contractRoles)) {
    return null;
  }

  for (const role of contractRoles) {
    const normalized = normalizeDashboardRole(role);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

function normalizeContractPermission(
  value: boolean | null | undefined,
  fallback: boolean,
): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  return fallback;
}

export function resolveDashboardBootstrapSnapshot(
  sessionState: DashboardBootstrapSessionStateLike,
): DashboardBootstrapSnapshot {
  const role = normalizeDashboardRole(sessionState.data?.user?.role);
  const sessionUser = sessionState.data?.user
    ? {
        id: sessionState.data.user.id ?? null,
        name: sessionState.data.user.name ?? null,
        email: sessionState.data.user.email ?? null,
      }
    : null;
  const hasExplicitWorkspaceAccess = sessionState.data?.user?.hasWorkspaceAccess;
  const requiresWorkspaceAccess =
    role !== "superadmin" && hasExplicitWorkspaceAccess === false;

  return {
    isAuthenticated: Boolean(sessionUser),
    isLoading: sessionState.isPending,
    role,
    workspaceAccessState: requiresWorkspaceAccess
      ? "request_access_required"
      : "granted",
    requiresWorkspaceAccess,
    sessionUser,
    permissions: getDashboardPermissions(role),
  };
}

export function normalizeDashboardBootstrapContractResponse(
  payload: DashboardBootstrapContractPayload,
): DashboardBootstrapSnapshot {
  const role = normalizeContractRole(payload.currentUser?.roles);
  const fallbackPermissions = getDashboardPermissions(role);
  const explicitWorkspaceAccess = payload.currentUser?.hasWorkspaceAccess;
  const requiresWorkspaceAccess =
    role !== "superadmin" && explicitWorkspaceAccess === false;

  return {
    isAuthenticated: Boolean(payload.currentUser?.id),
    isLoading: false,
    role,
    workspaceAccessState: requiresWorkspaceAccess
      ? "request_access_required"
      : "granted",
    requiresWorkspaceAccess,
    sessionUser: payload.currentUser
      ? {
          id: payload.currentUser.id ?? null,
          name: payload.currentUser.name ?? null,
          email: payload.currentUser.email ?? null,
        }
      : null,
    permissions: {
      canCreateProjects: normalizeContractPermission(
        payload.permissions?.canCreateProjects,
        fallbackPermissions.canCreateProjects,
      ),
      canUploadAssets: normalizeContractPermission(
        payload.permissions?.canUploadAssets,
        fallbackPermissions.canUploadAssets,
      ),
      canSendMessages: normalizeContractPermission(
        payload.permissions?.canSendMessages,
        fallbackPermissions.canSendMessages,
      ),
      canManageSettings: normalizeContractPermission(
        payload.permissions?.canManageSettings,
        fallbackPermissions.canManageSettings,
      ),
    },
  };
}

export async function fetchDashboardBootstrapContractSnapshot(
  adapter: DashboardBootstrapBackendAdapter,
): Promise<DashboardBootstrapSnapshot> {
  const response = await adapter.getBootstrap();

  if (!response.success || !response.data) {
    throw new Error(
      response.error?.message ??
        "Failed to load dashboard bootstrap contract.",
    );
  }

  return normalizeDashboardBootstrapContractResponse(response.data);
}

export const dashboardBootstrapBackendAdapter: DashboardBootstrapBackendAdapter = {
  async getBootstrap() {
    const response = await authenticatedFetch(buildApiUrl('/api/admin/dashboard/bootstrap'), {
      method: 'GET',
    });
    const payload = (await response.json()) as DashboardBootstrapContractResponse;

    if (!response.ok) {
      return {
        success: false,
        error: {
          code: payload.error?.code ?? 'BOOTSTRAP_UNAVAILABLE',
          message: payload.error?.message ?? 'Failed to load dashboard bootstrap contract.',
        },
      };
    }

    return payload;
  },
};

// Keep all shell-only derivations in one place so the router and tests share the same contract.
export function buildDashboardBootstrapViewModel(
  snapshot: DashboardBootstrapSnapshot,
  sessionNotice: string | null,
): DashboardBootstrapViewModel {
  const role = snapshot.role;

  return {
    ...snapshot,
    badge: role ? getDashboardBadge(role) : null,
    displayName: role
      ? getDashboardDisplayName(role)
      : getDashboardDisplayName("client"),
    canMutate:
      snapshot.permissions.canCreateProjects ||
      snapshot.permissions.canUploadAssets,
    isSuperadmin: role === "superadmin",
    sessionNotice,
  };
}

export function getDashboardInitialSettings(): DashboardSettingsState {
  return { ...DASHBOARD_SETTINGS };
}

export function getDashboardInitialThreads(): DashboardThread[] {
  return getSeededDashboardMessages().threads;
}

export function getDashboardSectionSubtitle(role: DashboardRole): string {
  return getDashboardSubtitle(role);
}
